import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ShieldCheck } from 'lucide-react'
import api from '@/lib/api/axios'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { formatDateTime } from '@/lib/dateUtils'
import { useMe } from '@/features/auth/hooks/useAuth'
import { useObjectVersions } from '@/features/objects/hooks'
import { AnalyticsReportDialog } from '@/features/objects/components/AnalyticsReportDialog'
import type { ScanStatusChange } from '@/lib/utils/scan-status-change'

/**
 * Scan status changes: what is waiting for a second person, and the record of what was decided.
 *
 * Andrew, 2026-10-05: "Build the waiting for approval screen". Changing a version to green takes
 * two people; until this screen an approver could find a request only by the clock beside a
 * version in an object's list.
 *
 * There is no Approve button on a row, on purpose. Approving says "I have looked at what the scan
 * found and I agree it is not valid", so the way to approve is Review: it opens the scan report,
 * with the findings, and the Approve and Reject controls are there beside them
 * (ScanStatusChangePanel). A list that let someone approve ten rows without opening one would be
 * the second person in name only.
 */

export interface ListedScanStatusChange extends ScanStatusChange {
    objectDescription: string | null
    versionExists: boolean
    versionDatetime: string | null
    versionType: string | null
}

type Which = 'pending' | 'decided'

export const scanStatusChangeKeys = {
    all: ['scan-status-changes'] as const,
    list: (which: Which) => ['scan-status-changes', 'list', which] as const,
}

function useListedChanges(which: Which) {
    return useQuery({
        queryKey: scanStatusChangeKeys.list(which),
        queryFn: async () => {
            const res = await api.get<{ data: ListedScanStatusChange[] }>('/api/scan-status-changes', { params: { state: which } })
            return res.data?.data ?? []
        },
        // Someone else may ask, or decide, while this is open.
        refetchInterval: 60_000,
    })
}

export function ScanStatusChangesPage() {
    const { t, i18n } = useTranslation()
    const { data: me } = useMe()
    const [which, setWhich] = useState<Which>('pending')
    const [reviewing, setReviewing] = useState<ListedScanStatusChange | null>(null)

    const waiting = useListedChanges('pending')
    const decided = useListedChanges('decided')
    const shown = which === 'pending' ? waiting : decided
    const rows = shown.data ?? []

    // The report belongs to a version, and the version's list carries it: its findings, its status
    // now, and every change asked of it. Read fresh, so the dialog shows a decision as it is made.
    const versions = useObjectVersions(reviewing?.objectId ?? null)
    const version = reviewing ? (versions.data ?? []).find((v) => v.versionID === reviewing.versionId) ?? null : null

    const when = (d: string | null | undefined) => (d ? formatDateTime(d, i18n.language) : '')
    const who = (p: { name: string | null } | null | undefined) => p?.name || t('objects.statusChange.someone')

    return (
        <div className="container mx-auto p-6 space-y-5">
            <div className="flex items-start gap-3">
                <ShieldCheck className="h-7 w-7 flex-shrink-0 mt-0.5 text-muted-foreground" />
                <div>
                    <h1 className="text-2xl font-bold">{t('scanStatusChanges.title')}</h1>
                    <p className="text-sm text-muted-foreground max-w-3xl">{t('scanStatusChanges.description')}</p>
                </div>
            </div>

            <Tabs value={which} onValueChange={(v) => setWhich(v as Which)}>
                <TabsList>
                    <TabsTrigger value="pending">
                        {t('scanStatusChanges.tabWaiting')}
                        {waiting.data && <span className="ml-1.5 tabular-nums">({waiting.data.length})</span>}
                    </TabsTrigger>
                    <TabsTrigger value="decided">{t('scanStatusChanges.tabHistory')}</TabsTrigger>
                </TabsList>
            </Tabs>

            {shown.isLoading ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : shown.isError ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">{t('scanStatusChanges.loadFailed')}</p>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
                    {t(which === 'pending' ? 'scanStatusChanges.emptyWaiting' : 'scanStatusChanges.emptyHistory')}
                </div>
            ) : (
                <div className="rounded-lg border overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('scanStatusChanges.colObject')}</TableHead>
                                <TableHead>{t('scanStatusChanges.colVersion')}</TableHead>
                                <TableHead>{t('scanStatusChanges.colChange')}</TableHead>
                                <TableHead>{t('scanStatusChanges.colRequested')}</TableHead>
                                <TableHead className="min-w-[16rem]">{t('scanStatusChanges.colReason')}</TableHead>
                                {which === 'decided' && <TableHead>{t('scanStatusChanges.colOutcome')}</TableHead>}
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((c) => {
                                const mine = c.requestedBy?.id != null && Number(c.requestedBy.id) === Number(me?.id)
                                return (
                                    <TableRow key={c.id} data-testid={`change-${c.id}`}>
                                        <TableCell className="align-top">
                                            <div className="font-medium">{c.objectId}</div>
                                            {c.objectDescription && <div className="text-xs text-muted-foreground">{c.objectDescription}</div>}
                                        </TableCell>
                                        <TableCell className="align-top">
                                            <div className="text-xs font-mono break-all">{c.objectName}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {[c.versionType, when(c.versionDatetime)].filter(Boolean).join(' · ')}
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top">
                                            {/* What the scan said, and what is asked for. Shape as well as colour. */}
                                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                                <AnalyticsStatusIndicator status={c.previousStatus} size="h-4 w-4" showIcon />
                                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                                <AnalyticsStatusIndicator status={c.newStatus} size="h-4 w-4" showIcon />
                                            </span>
                                        </TableCell>
                                        <TableCell className="align-top whitespace-nowrap">
                                            <div>{who(c.requestedBy)}</div>
                                            <div className="text-xs text-muted-foreground">{when(c.requestedAt)}</div>
                                            {mine && which === 'pending' && (
                                                <div className="mt-1 text-[11px] font-medium" title={t('objects.statusChange.ownRequest')}>
                                                    {t('scanStatusChanges.yours')}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="align-top">
                                            <p className="text-xs whitespace-pre-wrap break-words line-clamp-4" title={c.reason}>{c.reason}</p>
                                        </TableCell>
                                        {which === 'decided' && (
                                            <TableCell className="align-top">
                                                <div className="text-xs font-medium">{t(`objects.statusChange.state.${c.state}`)}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {c.revokedAt
                                                        ? `${who(c.revokedBy)} · ${when(c.revokedAt)}`
                                                        : `${who(c.decidedBy)} · ${when(c.decidedAt)}`}
                                                </div>
                                            </TableCell>
                                        )}
                                        <TableCell className="align-top text-right">
                                            {c.versionExists ? (
                                                <Button size="sm" variant={which === 'pending' && !mine ? 'default' : 'outline'} className="h-8" onClick={() => setReviewing(c)}>
                                                    {t(which === 'pending' ? 'scanStatusChanges.review' : 'scanStatusChanges.open')}
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">{t('scanStatusChanges.versionGone')}</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}

            {reviewing && versions.isLoading && (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            )}
            {reviewing && !versions.isLoading && !version && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">{t('scanStatusChanges.versionGone')}</p>
            )}
            <AnalyticsReportDialog
                open={!!reviewing && !!version}
                onOpenChange={(open) => { if (!open) setReviewing(null) }}
                analyticsText={version?.analyticsText || null}
                version={version}
                objectId={reviewing?.objectId}
                objectDescription={reviewing?.objectDescription}
            />
        </div>
    )
}
